import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import CircledExclamationmarkIcon from 'icons/CircledExclamationmark.icon';
import { useTranslation } from 'react-i18next';
import { DownloadBannerStatus, DOWNLOAD_BANNER_STATUS } from 'models/Download';
import { Loader } from 'ncoded-component-library';
import BannerWrapper from 'components/BannerWrapper';
import { COLOR_VARIANT } from 'models/Colors';
import toast, { Toast } from 'react-hot-toast';
import useToast from 'hooks/useToast';

import './DownloadStatusBanner.styles.scss';

const BANNER_TIME = 5000;

export type DownloadStatusBannerRef = {
  open: () => void;
  close: () => void;
};

type DownloadStatusBannerProps = {
  className?: string;
  variant: DownloadBannerStatus;
  onClose: () => void;
};

const DownloadStatusBanner: ForwardRefRenderFunction<
  DownloadStatusBannerRef,
  DownloadStatusBannerProps
> = (props, ref) => {
  const { className, variant, onClose } = props;

  const [isVisible, setIsVisible] = useState(false);

  const classes = classNames('download-status-banner', variant, className);

  const { t } = useTranslation();

  useImperativeHandle(
    ref,
    () => ({
      open: () => setIsVisible(true),
      close: () => setIsVisible(false),
    }),
    [],
  );

  const color = useMemo(() => {
    if (!isVisible) return null;

    if (variant === DOWNLOAD_BANNER_STATUS.PREPARING) {
      return COLOR_VARIANT.LIGHT_GREY;
    }

    if (variant === DOWNLOAD_BANNER_STATUS.SUCCESS) {
      return COLOR_VARIANT.LIGHT_GREEN;
    }
    return COLOR_VARIANT.RED;
  }, [variant, isVisible]);

  const icon = useMemo(() => {
    if (!isVisible) return null;

    if (variant === DOWNLOAD_BANNER_STATUS.PREPARING) {
      return <Loader overlay={false} size="small" />;
    }
    if (variant === DOWNLOAD_BANNER_STATUS.SUCCESS) {
      return <CheckcircleIcon />;
    }
    return <CircledExclamationmarkIcon />;
  }, [variant, isVisible]);

  const message = useMemo(() => {
    if (!isVisible) return null;
    if (variant === DOWNLOAD_BANNER_STATUS.PREPARING) {
      return t('preparingFilesForDownload');
    }

    if (variant === DOWNLOAD_BANNER_STATUS.SUCCESS) {
      return t('downloadInitiated');
    }

    return t('thereWasAnErrorWhilePreparingYourDownloading');
  }, [variant, isVisible, t]);

  useEffect(() => {
    if (variant !== DOWNLOAD_BANNER_STATUS.SUCCESS) return;

    const timeout = setTimeout(onClose, BANNER_TIME);

    return () => clearTimeout(timeout);
  }, [variant, onClose]);

  const render = useCallback(
    ({ id, visible }: Toast) => (
      <BannerWrapper
        className={classes}
        isVisible={visible}
        color={color}
        onClose={() => {
          toast.dismiss(id);
          onClose();
        }}
      >
        <div className="download-status-banner__icon">{icon}</div>
        <p>{message}</p>
      </BannerWrapper>
    ),
    [classes, color, icon, message, onClose],
  );

  useToast({
    visible: isVisible && !!variant,
    render,
  });

  return null;
};

export default forwardRef(DownloadStatusBanner);
